import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {get} from "lodash";
import {ReactComponent as BallDots} from "apps/DRAW_PREDICTOR/assets/images/ballDots.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {ReactComponent as RoundArrow} from "apps/DRAW_PREDICTOR/assets/images/icons/roundArrow.svg";
import {Collapse} from "@mui/material";
import {Exist, PureButton} from "components";
import {LANGUAGE_PATH} from "modules";
import styled, {css} from "styled-components";
import {media} from "assets/css/media";
import {IQualifierGroup} from "apps/DRAW_PREDICTOR/modules/reducers";
import {Tooltip} from "apps/DRAW_PREDICTOR/pages/preDraw/Tooltip";
import {useDispatch} from "react-redux";
import {setPreDrawActiveIndex} from "apps/DRAW_PREDICTOR/modules/actions";

interface IGroupProps {
	group: IQualifierGroup;
}

interface IDisabled {
	isDisabled: boolean;
}

const Title = styled.h2`
	font-family: "Qatar2022";
	font-style: normal;
	font-weight: 700;
	font-size: 34px;
	line-height: 80%;
	color: #fff;
	@media (max-width: ${media.mobile}) {
		font-size: 20px;
		line-height: 80%;
		margin-bottom: 10px;
	}
`;
const SubTitle = styled.h3`
	font-family: "Graphik";
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	color: #fff;
	display: flex;
	align-items: center;
	svg {
		margin-inline-start: 10px;
	}
	@media (max-width: ${media.mobile}) {
		font-size: 14px;
		line-height: 120%;
		svg {
			margin-inline-start: 5px;
			width: 70px;
		}
	}
	@media (max-width: 320px) {
		svg {
			width: 40px;
		}
	}
`;
const Flex = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 20px;
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 20px;
`;

const Info = styled(PureButton)`
	background: #eeeee4;
	border-radius: 10px;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #000;
	position: relative;
	&:hover:enabled {
		background: #ff004c;
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
		color: #ffffff;
	}
	&:disabled {
		color: rgba(255, 255, 255, 0.2);
		background: rgba(238, 238, 238, 0.2);
		cursor: default;
	}
	@media (max-width: 320px) {
		width: 35px;
		height: 35px;
	}
`;
const Toggle = styled(Info)`
	transition: 0.25s;

	&.closed {
		transform: rotate(180deg);
	}
	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;

const NextButton = styled(PureButton)`
	text-align: center;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	margin: 20px auto;
	font-weight: 500;
	font-size: 14px;
	line-height: 130%;
	width: 100px;
	svg {
		margin-top: 5px;
	}
	@media (max-width: ${media.mobile}) {
		display: none;
	}
`;
const getStyles = ({isDisabled}: IDisabled) => {
	return isDisabled
		? css`
				${Title},
				${SubTitle},
			${NextButton} {
					opacity: 0.2;
				}
		  `
		: css``;
};

const CardWrapper = styled.div<IDisabled>`
	${getStyles}
`;

interface INext extends IDisabled {
	onClick: () => void;
}

const Next: React.FC<INext> = ({isDisabled, onClick}) => {
	const {t} = useTranslation(LANGUAGE_PATH);

	return (
		<NextButton disabled={isDisabled} onClick={onClick}>
			<span>{t("prediction.page.qualifying_next")}</span>
			<RoundArrow />
		</NextButton>
	);
};

export const DoubleTeamCard = styled.div`
	svg {
		max-width: 100%;
	}
	@media (max-width: ${media.mobile}) {
		width: calc(50% - 10px);
	}
`;
export const TeamCard = styled.div`
	svg {
		max-width: 100%;
	}
	@media (max-width: ${media.mobile}) {
		width: calc(25% - 10px);
	}
`;

export const Cards = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 20px;
	@media (max-width: ${media.mobile}) {
		gap: 10px;
		&.single-team {
			justify-content: center;
		}
	}
`;

interface ICard extends IDisabled, IGroupProps {
	isLast: boolean;
	tooltip?: string;
	index: number;
}

export const Card: React.FC<ICard> = ({isDisabled, group, children, isLast, tooltip, index}) => {
	const [open, setOpen] = useState(true);
	const [showTooltip, setShowTooltip] = useState(false);
	const dispatch = useDispatch();
	const toggle = () => {
		setOpen(!open);
	};
	const toggleTooltip = () => {
		setShowTooltip(!showTooltip);
	};

	const showNextGroup = () => {
		dispatch(setPreDrawActiveIndex(index + 1));
	};
	const {i18n} = useTranslation();
	const title = get(group.name, i18n.language, "") as string;
	const description = get(group.description, i18n.language, "") as string;
	const className = open ? "" : "closed";

	return (
		<CardWrapper isDisabled={isDisabled}>
			<Title>{title}</Title>
			<Flex>
				<SubTitle>
					{`${description} `} <BallDots />
				</SubTitle>
				<Actions>
					<Info disabled={isDisabled} onClick={toggleTooltip}>
						<InfoOutlinedIcon />
						<Exist when={showTooltip}>
							<Tooltip onClick={toggleTooltip} tooltip={tooltip} />
						</Exist>
					</Info>
					<Toggle onClick={toggle} className={className} disabled={isDisabled}>
						<RoundArrow />
					</Toggle>
				</Actions>
			</Flex>
			<Collapse in={open}>
				{children}
				<Exist when={!isLast}>
					<Next onClick={showNextGroup} isDisabled={isDisabled} />
				</Exist>
			</Collapse>
		</CardWrapper>
	);
};
